import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Modal from "@/components/modal";
import FileUploadInput from "@/components/forms/inputs/upload";
import FindingForm from "@/components/forms/findingForm";
import GeneralListItem from "@/components/general/generaListItem";
import ImageViewer from "@/components/general/imageViewer";
import TodoNoteImageThumbnailsWrapper from "@/components/todos/todoNoteImageThumbnailsWrapper";
import {
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { api } from "@/helper/api";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/components/state/store";
import { setTodoIncludeDoneTodos } from "@/components/state/todo/todoSlice";
import HighFiveIcon from "@/icons/highFiveIcon";
import dayjs from "dayjs";
import "@/routes/todo.css";
import FlagIcon from "@/icons/flagIcon";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import debounce from "lodash/debounce";
import CircularProgress from "@mui/material/CircularProgress";
import PageScrollTo from "@/components/general/pageScrollTo";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import DropDown from "@/components/forms/inputs/dropDown";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { parseGbuDescription } from "@/components/todos/gbuDescriptionUtils";
import { buildTuitionTodoOpenCourseUrl } from "@/components/todos/buildTuitionTodoOpenCourseUrl";
import { shouldBlockLmsForSubdomain } from "@/components/lms/lms.utils";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import TodoMetadataChips from "@/components/todos/todoMetadataChips";
import {
  resolveAppointmentDashboardTodoLink,
} from "@/components/appointmentDashboard/getAdminAppointmentDashboardUrl";

export default function Todo() {
  const [todos, setTodos]: any = useState([]);
  const [todosCount, setTodosCount] = useState<number>(0);
  const [hasMoreResults, setHasMoreResults] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [modalContent, setModalContent]: any = useState("");
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalType, setModalType] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortOption, setSortOption] = useState<string>("created_on");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const { handleError, errorModal } = useErrorModal();
  const [buildingOptions, setBuildingOptions] = useState<any[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const lastTodoRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();

  const selectedChildSubjectId = useSelector(
    (state: RootState) => state.parent.subjectId,
  );

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];

  const user = useSelector((state: RootState) => state.login);
  const hasLmsAccess = !shouldBlockLmsForSubdomain({
    subdomain,
    permissions: user.permissions,
  });
  const child = useSelector((state: RootState) => state.parent);
  const includeDoneTodosFromRedux = useSelector(
    (state: RootState) => state.todo?.includeDoneTodos ?? false,
  );

  const [showLoadingMoreSpinner, setShowLoadingMoreSpinner] =
    useState<boolean>(false);

  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentImageList, setCurrentImageList] = useState<any[]>([]);

  const limit = 20;

  const { t } = useTranslation();
  usePageHeading(t("your_todos"));
  const { showConfirmationModal, showConfirmationModalAsync } =
    useConfirmationModal();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const today = dayjs();

  const mapDangerousAnswer = (value?: string) => {
    if (!value) return "";
    const v = String(value).trim().toLowerCase();
    if (v === "yes" || v === "ja") return t("applies");
    if (v === "no" || v === "nein") return t("does_not_apply");
    return value;
  };

  const formatDescription = (description: any, type: string) => {
    if (type === "gbu") {
      const gbu = parseGbuDescription(description);
      if (!gbu) return description || "";
      return (
        <div>
          <div>
            <span className="boldText">{t("assessment_gbu_label")}:</span>
          </div>
          <div className="marginBottom_0_5">{gbu.assessment || ""}</div>
          <div>
            <span className="boldText">{t("dangerous_answer_gbu_label")}:</span>
          </div>
          <div className="marginBottom_0_5">
            {mapDangerousAnswer(gbu.dangerous_answer)}
          </div>
          <div>
            <span className="boldText">{t("possible_remedy_gbu_label")}:</span>
          </div>
          <div>{gbu.possible_remedy || ""}</div>
        </div>
      );
    }
    return description || "";
  };

  const getBuildings = (
    setBuildingOptions: any,
    subject: string,
    setError: any,
  ) => {
    const additionalUserParameters: any = {};
    additionalUserParameters["subject"] = subject;
    if (subdomain === "manage") {
      additionalUserParameters["show_managed"] = true;
    }
    api.genericApiRequest({
      entity: "building",
      method: "get",
      parametersToRender: {
        additionalUrlParameters: additionalUserParameters,
        depth: "0",
      },
      successHandler: (res: any) => {
        const options = [
          { value: "no_building", label: t("no_building_location") },
          ...res.data.results.map((item: any) => ({
            value: item.id,
            label:
              item.name + ` (${item.street_house}, ${item.zip} ${item?.city} )`,
          })),
        ];
        setBuildingOptions(options);
      },
      failHandler: (error: any) => {
        setError(error);
      },
    });
  };

  const refreshTodosFromServer = (
    searchTerm?: string,
    includeDoneTodosParam?: boolean,
    offset = 0,
    sortOption?: string,
    sortDirection?: "asc" | "desc",
  ) => {
    setShowLoadingMoreSpinner(true);
    const additionalUrlParameters: any = {
      limit,
      offset,
    };

    if (searchTerm) {
      additionalUrlParameters["search"] = searchTerm;
    }
    if (includeDoneTodosParam === false) {
      additionalUrlParameters["done"] = false;
    }
    if (sortOption) {
      additionalUrlParameters["ordering"] =
        sortDirection === "desc" ? `-${sortOption}` : sortOption;
    }
    if (selectedBuilding) {
      if (selectedBuilding.value === "no_building") {
        additionalUrlParameters["building__isnull"] = true;
      } else {
        additionalUrlParameters["building"] = selectedBuilding.value;
      }
    }

    if (
      subdomain === "admin" &&
      user.subjectId !== "" &&
      child.subjectId === ""
    ) {
      additionalUrlParameters["subject"] = user.subjectId;
    }

    //currently disabled - needs additional implementations for tagging todos

    // if (subdomain === "admin") {
    //   additionalUrlParameters["staff"] = true;
    // }
    api.genericApiRequest({
      entity: "todo",
      method: "get",
      parametersToRender: {
        additionalUrlParameters: additionalUrlParameters,
        depth: "0",
        expand: "marked_done_by",
      },
      successHandler: (res: any) => {
        setTodosCount(res.data.count);
        setTodos((prevTodos: any) =>
          offset === 0 ? res.data.results : [...prevTodos, ...res.data.results],
        );
        setShowLoadingMoreSpinner(false);
        setHasMoreResults(res.data.next !== null);
      },
      failHandler: (error: any) => {
        handleError(error);
        setShowLoadingMoreSpinner(false);
      },
    });
  };

  const delayedSearch = useCallback(
    debounce((searchTerm: string, includeDoneTodosParam: boolean) => {
      refreshTodosFromServer(searchTerm, includeDoneTodosParam);
    }, 250),
    [],
  );

  useEffect(() => {
    setTodos([]);
    setHasMoreResults(true);
    const resetOffset = 0;

    if (searchTerm.length === 0) {
      refreshTodosFromServer(
        undefined,
        includeDoneTodosFromRedux,
        resetOffset,
        sortOption,
        sortDirection,
      );
    } else if (searchTerm.length >= 3) {
      delayedSearch(searchTerm, includeDoneTodosFromRedux);
    }

    return () => delayedSearch.cancel();
  }, [
    searchTerm,
    includeDoneTodosFromRedux,
    delayedSearch,
    sortOption,
    sortDirection,
    selectedBuilding,
  ]);

  useEffect(() => {
    if (!lastTodoRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !showLoadingMoreSpinner &&
          hasMoreResults
        ) {
          if (todos.length < todosCount) {
            const newOffset = todos.length;
            refreshTodosFromServer(
              searchTerm,
              includeDoneTodosFromRedux,
              newOffset,
              sortOption,
              sortDirection,
            );
          } else {
            if (lastTodoRef.current) {
              observer.disconnect();
            }
          }
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      },
    );

    observer.observe(lastTodoRef.current);

    return () => {
      if (lastTodoRef.current) {
        observer.unobserve(lastTodoRef.current);
      }
    };
  }, [
    lastTodoRef.current,
    showLoadingMoreSpinner,
    searchTerm,
    limit,
    todos.length,
    todosCount,
    includeDoneTodosFromRedux,
    hasMoreResults,
    sortOption,
    sortDirection,
  ]);

  const resetModal = () => {
    setModalOpen(false);
  };

  const finishTodo = (todoItem: any, onSuccessHandler?: any) => {
    const result = { impersonate_subject: selectedChildSubjectId };
    const additionalRouteParts: any = {};
    additionalRouteParts["mark_done"] = "/";
    api.genericApiRequest({
      entity: "todo",
      method: "put",
      successHandler: () => {
        setTodos((prevTodos: any) =>
          prevTodos.filter((todo: any) => todo.id !== todoItem.id),
        );
        if (onSuccessHandler) {
          onSuccessHandler();
        }
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      entityId: todoItem.id,
      additionalRouteParts: additionalRouteParts,
      submitData: result,
    });
  };

  const renderModalUploadContent = (todoItem: any) => {
    return (
      <FileUploadInput
        multiple={false}
        name="file"
        target_filename={todoItem.target.filename}
        folderId={todoItem.target.folder}
        onCancel={() => {
          resetModal();
        }}
        handleUploadSuccess={(modalOpen: boolean) => {
          if (modalOpen) {
            finishTodo(todoItem, () => {
              refreshTodosFromServer();
            });
            resetModal();
          }
        }}
        onUploadStateChange={setUploadInProgress}
      />
    );
  };

  const renderModalFormContent = (todoItem: any) => {
    return (
      <FindingForm
        withPaper={false}
        findingId={todoItem.target.finding}
        formId={todoItem.target.form}
        preSubmitConfirmMessage={t("confirm_submit_finding_form")}
        onSuccessfulFormSubmitHandler={() => {
          finishTodo(todoItem, () => {
            resetModal();
            refreshTodosFromServer();
          });
        }}
        onCancelFormHandler={() => {
          resetModal();
        }}
        onCloseModal={() => {
          resetModal();
        }}
      />
    );
  };

  const getModalContent = (todoItem: any) => {
    let modalContent = <></>;
    switch (todoItem.type) {
      case "upload":
        modalContent = renderModalUploadContent(todoItem);
        break;
      case "form":
        modalContent = renderModalFormContent(todoItem);
        break;
    }

    return modalContent;
  };

  const openTodoActionInModal = (todoItem: any) => {
    setModalContent(getModalContent(todoItem));
    setModalOpen(true);
    setModalTitle(todoItem.target.filename);
    setModalType(todoItem.type);
  };

  const handleImageClick = (images: any[], imageIndex: number) => {
    setCurrentImageList(images);
    setSelectedImageIndex(imageIndex);
    setImageViewerOpen(true);
  };

  const filteredAndSortedTodos = useMemo(() => {
    return includeDoneTodosFromRedux
      ? todos
      : todos.filter((todo: any) => !todo.done);
  }, [todos, includeDoneTodosFromRedux]);

  let todosList;

  if (todos.length === 0 && !searchTerm) {
    todosList = (
      <Grid container>
        <Grid item component={Paper} className="allDoneContainer">
          <h1 className="allDoneHeading">{t("all_done")}</h1>
          <div className="allDoneContentWrapper">
            <h4 className="allDoneContentHeading">
              <Trans i18nKey={"todo_all_done_message"} />
            </h4>
            <HighFiveIcon width="160" height="159" />
          </div>
        </Grid>
      </Grid>
    );
  } else if (filteredAndSortedTodos.length === 0 && searchTerm) {
    todosList = (
      <Grid container>
        <Grid item component={Paper} className="noSearchResultsContainer">
          <h1 className="noSearchResultsHeading">{t("no_results_found")}</h1>
          <HeartBrokenIcon color="secondary" className="noSearchResultsIcon" />
          <div className="noSearchResultsContentWrapper">
            <h4 className="noSearchResultsContentHeading">
              {t("todo_no_matching_entries_message", { searchTerm })}
            </h4>
          </div>
        </Grid>
      </Grid>
    );
  } else {
    todosList = (
      <Grid container item>
        <Grid container item sm={12} component={List} rowSpacing={"1rem"}>
          {filteredAndSortedTodos.map((todoItem: any, index: number) => {
            const remainingDays = dayjs(todoItem.due_date).diff(today, "day");
            const buttonsConfig = [];

            if (!todoItem.done) {
              if (todoItem.type === "text" || todoItem.type === "gbu") {
                buttonsConfig.push({
                  tooltip: t("mark_todo_as_done"),
                  icon: "checkmark",
                  backgroundColor: "#ffffff",
                  iconColor: "#73be2a",
                  className: "todoOutlinedRoundButton todoMarkDoneButton",
                  onClick: async () => {
                    const isConfirmed = await showConfirmationModalAsync(
                      t("mark_todo_as_done_question"),
                    );
                    if (isConfirmed) {
                      finishTodo(todoItem, () => {
                        refreshTodosFromServer();
                      });
                    }
                  },
                });

                if (
                  todoItem.target &&
                  todoItem.target.link &&
                  todoItem.target.linkTitle
                ) {
                  buttonsConfig.push({
                    tooltip: todoItem.target.linkTitle,
                    icon: "arrowRight",
                    backgroundColor: "#ffffff",
                    iconColor: "#ED6C02",
                    className: "todoOutlinedRoundButton todoOpenActionButton",
                    onClick: () => {
                      window.open(
                        resolveAppointmentDashboardTodoLink(
                          todoItem.target.link,
                        ),
                        "_blank",
                        "noopener,noreferrer",
                      );
                    },
                  });
                }
              } else if (todoItem.type === "tuition") {
                if (todoItem.target?.course && hasLmsAccess) {
                  buttonsConfig.push({
                    tooltip: t("todo_open_course"),
                    icon: "arrowRight",
                    backgroundColor: "#ffffff",
                    iconColor: "#ED6C02",
                    className: "todoOutlinedRoundButton todoOpenActionButton",
                    onClick: () => {
                      const url = buildTuitionTodoOpenCourseUrl(
                        String(todoItem.target.course),
                        subdomain,
                      );
                      const fullUrl = /^https?:\/\//.test(url)
                        ? url
                        : `${window.location.origin}${url}`;
                      window.open(fullUrl, "_blank", "noopener,noreferrer");
                    },
                  });
                }
              } else {
                buttonsConfig.push({
                  icon: "arrowRight",
                  backgroundColor: "#ffffff",
                  iconColor: "#ED6C02",
                  className: "todoOutlinedRoundButton todoOpenActionButton",
                  onClick: () => {
                    openTodoActionInModal(todoItem);
                  },
                });
              }
            }

            const isSecondLast = index === filteredAndSortedTodos.length - 2;

            return (
              <Grid
                data-testid={`todo-item-${todoItem.id}`}
                item
                sm={12}
                key={`${todoItem.id}-${index}`}
                className="todoItem"
                ref={isSecondLast ? lastTodoRef : null}
              >
                <GeneralListItem
                  key={`${todoItem.id}-${index}`}
                  buttonsConfig={buttonsConfig}
                  wrapperClassName={`generalListItemWrapper${
                    todoItem.done ? " todoItemDone" : ""
                  }`}
                  allowHover={false}
                  buttonWidth={isMobile ? "40vw" : undefined}
                  borderRadius={isMobile ? "0.5rem" : undefined}
                >
                  <div className="todoItemContentWrapper">
                    <ListItem className="todoListItem">
                      <ListItemText
                        primary={todoItem.title}
                        primaryTypographyProps={{
                          className: "todoItemTitleText",
                        }}
                      />
                      <ListItemText
                        secondary={formatDescription(
                          todoItem.description,
                          todoItem.type,
                        )}
                        className="todoItemDescription"
                        style={{ whiteSpace: "pre-line" }}
                      />
                      <TodoMetadataChips
                        done={todoItem.done}
                        completedOn={todoItem.marked_done_on}
                        completedByName={todoItem.marked_done_by?.name}
                        createdByName={todoItem.created_by?.name}
                        showCompletedBy={subdomain === "manage"}
                        showCreatedBy={subdomain === "admin"}
                      />
                      <ListItemText
                        className="todoListItemText"
                        primary={
                          <div className="listItemTextWrapper">
                            {!todoItem.done && (
                              <Typography
                                component={"span"}
                                color="text.primary"
                                className="remainingDays"
                              >
                                <span className="remainingDaysIconWrapper">
                                  <FlagIcon width="12" height="14" />
                                </span>
                                {remainingDays < 0 ? (
                                  t("overdue_exclamation")
                                ) : (
                                  <Trans
                                    i18nKey={
                                      remainingDays <= 1
                                        ? t("remaining_day", {
                                            day: remainingDays,
                                          })
                                        : t("remaining_days", {
                                            days: remainingDays,
                                          })
                                    }
                                  />
                                )}
                              </Typography>
                            )}
                            <Typography color="text.secondary">
                              {t("todo_until", {
                                date: dayjs(todoItem.due_date).format(
                                  "DD.MM.YYYY",
                                ),
                              })}
                            </Typography>
                            {todoItem?.building && (
                              <div className="locationWrapper">
                                <LocationOnIcon className="locationIcon" />
                                <Typography color="text.secondary">
                                  {todoItem.building.name +
                                    " (" +
                                    todoItem.building.street_house +
                                    ", " +
                                    todoItem.building.zip +
                                    " " +
                                    todoItem.building.city +
                                    ")"}
                                </Typography>
                              </div>
                            )}
                          </div>
                        }
                      />
                      <TodoNoteImageThumbnailsWrapper
                        note={todoItem.note}
                        images={todoItem.image_set}
                        onImageClick={(imageIndex) =>
                          handleImageClick(todoItem.image_set, imageIndex)
                        }
                        childrenSubjectId={String(selectedChildSubjectId)}
                        customThumbnailimageAlt={t("image")}
                      />
                    </ListItem>
                  </div>
                </GeneralListItem>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    );
  }

  const searchFilterSortSection = (
    <Grid item xs={12} className="searchFilterSortWrapper">
      <Grid item xs={12}>
        <div className="searchField">
          <SearchIcon className="searchFieldStartIcon" />
          <InputWithRef
            name="todo-search"
            placeholder={t("search_todos")}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="searchFieldControl"
            inputClassname="searchFieldInput"
            hideLabel
            dataTestId="Todo-searchField"
          />
          {searchTerm && (
            <CloseIcon
              className="searchFieldClearIcon"
              onClick={() => setSearchTerm("")}
            />
          )}
        </div>
      </Grid>
      <Grid
        item
        xs={12}
        container
        rowSpacing={"1rem"}
        className="filterSortContainer"
      >
        <Grid
          item
          xs={12}
          sm={6}
          md={subdomain === "portal" ? 6 : 4}
          className="sortSelectContainer"
        >
          <DropDown
            name="sort"
            label={t("sort_by")}
            defaultValue={sortOption}
            onChange={(value) =>
              setSortOption(value ? value.value : "created_on")
            }
            options={[
              { value: "created_on", label: t("creation_date") },
              { value: "due_date", label: t("due_datetime") },
            ]}
          />
        </Grid>
        {subdomain !== "portal" && (
          <Grid item xs={12} sm={6} md={4} className="buildingSelectContainer">
            <DropDown
              options={buildingOptions}
              onChange={(value) => setSelectedBuilding(value)}
              name="building"
              label={t("filter_by_building_location")}
              defaultValue={selectedBuilding}
              isClearable={true}
              showLoadingIndicator={false}
            />
          </Grid>
        )}
        <Grid
          item
          xs={12}
          sm={12}
          md={subdomain === "portal" ? 6 : 4}
          className="sortDirectionContainer"
        >
          <Typography>{t("sort_direction")}</Typography>
          <ToggleButtonGroup
            value={sortDirection}
            exclusive
            onChange={(e, newDirection) => {
              if (newDirection) setSortDirection(newDirection);
            }}
            aria-label={t("sort_direction")}
            className="sortDirectionToggle"
            fullWidth
            sx={{
              borderRadius: "0.5rem",
              "& .MuiToggleButton-root": {
                borderRadius: 0,
              },
              "& .MuiToggleButtonGroup-grouped:first-of-type": {
                borderTopLeftRadius: "0.5rem",
                borderBottomLeftRadius: "0.5rem",
              },
              "& .MuiToggleButtonGroup-grouped:last-of-type": {
                borderTopRightRadius: "0.5rem",
                borderBottomRightRadius: "0.5rem",
              },
            }}
          >
            <ToggleButton
              value="asc"
              aria-label={t("ascending")}
              className="sortDirectionToggleButton"
            >
              <ArrowUpwardIcon />
              {t("ascending")}
            </ToggleButton>
            <ToggleButton
              data-testid="todo-sort-descending"
              value="desc"
              aria-label={t("descending")}
              className="sortDirectionToggleButton"
            >
              <ArrowDownwardIcon />
              {t("descending")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>
      <Grid item xs={12} className="doneFilterContainer">
        <FormControlLabel
          control={
            <Switch
              checked={includeDoneTodosFromRedux}
              onChange={(e) => {
                dispatch(setTodoIncludeDoneTodos(e.target.checked));
              }}
              color="secondary"
            />
          }
          label={t("include_done_todos")}
          labelPlacement="start"
          className="doneFilterSwitch"
        />
      </Grid>
    </Grid>
  );

  const bottomSpinner = showLoadingMoreSpinner && (
    <Grid container className="bottomSpinnerWrapper">
      <CircularProgress
        size={30}
        thickness={5}
        color="secondary"
        className="loadingMoreSpinner"
      />
    </Grid>
  );

  const handleModalOnClose = () => {
    showConfirmationModal(t("confirm_modal_close"), {
      title: "",
      confirmButtonText: "",
      onConfirm: () => setModalOpen(false),
    });
  };

  useEffect(() => {
    if (user.subjectId && subdomain !== "portal") {
      let subjectId = user.subjectId;

      if (child?.subjectId !== "") {
        subjectId = child.subjectId;
      }
      getBuildings(setBuildingOptions, String(subjectId), handleError);
    }
  }, [user.subjectId, subdomain]);

  return (
    <>
      <Grid container>
        {searchFilterSortSection}
        {todosList}
        {bottomSpinner}
      </Grid>
      <Modal
        open={modalOpen}
        title={modalTitle}
        hideDialogActions={true}
        disableCancelButton={uploadInProgress}
        onClose={handleModalOnClose}
        maxWidth={modalType === "upload" ? "40rem" : undefined}
      >
        {modalContent}
      </Modal>
      <ImageViewer
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        images={currentImageList}
        initialImageIndex={selectedImageIndex}
        childrenSubjectId={String(selectedChildSubjectId)}
        doNotShowFileName={true}
      />
      {errorModal}
      <PageScrollTo
        scrollDirection="up"
        showButtonCondition={todos.length >= 10}
        position={isMobile ? "right" : "center"}
      />
    </>
  );
}
